import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { useGetOfferAvailableAddons } from '@/hooks/useOffer'
import { useSaveOfferAddons } from '@/hooks/useOfferPackage'
import { LabelHintIcon } from '@/icons'
import { Box, Button, Grid, Text } from '@chakra-ui/react'
import type { AddonDescriptionProps, OfferPackage, OfferProperty, OfferPropertyAddition, OfferTravel, OfferTravelAddition, PackageItemModel, PartPackage } from 'BrokerTsLib/src/dtos'
import { useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { AddonCheckbox } from './AddonCheckbox'

export const OfferPackageSettingsFormAddons = ({
  offer,
  isReadOnly,
  submitCallback
}: {
  offer: OfferPackage | OfferProperty,
  isReadOnly: boolean,
  submitCallback?: () => void
}) => {
  const { product, app } = useApp()
  const { data } = useGetOfferAvailableAddons(offer.offerId)
  const { mutateAsync: saveOfferAddons, isPending: isLoading } = useSaveOfferAddons()
  const addons = (data?.data ?? []) as PackageItemModel[]
  const [addonDescription, setAddonDescription] = useState<AddonDescriptionProps | null>(null)

  // prazdny objekt pro ostatni, aby bylo mozne pouzit komponentu pro vice produktu nez jen majetek
  const dv = useMemo(() => {
    if (app == Apps.property)
      return (offer as OfferProperty).additionData

    if (app == Apps.travel)
      return (offer as OfferTravel).additionData

    return {}
  }, [app, offer])

  const methods = useForm<OfferPropertyAddition | OfferTravelAddition>({
    defaultValues: {
      ...dv,
      changedParts: {},
    }
  })

  const {
    handleSubmit,
    watch,
    setValue

  } = methods

  const changedRisks = watch('changedParts')

  const submitForm = handleSubmit(({
    changedParts,
    ...restValues
  }) => {
    const addons = Object.keys(changedParts).map(x => (changedParts)[x as any])

    return saveOfferAddons({
      offerId: offer.offerId,
      addons: addons,
      additionData: restValues
    }).then(() => {
      submitCallback?.()
    })
  })

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={submitForm}>
          <Grid
            sx={{
              gridTemplateColumns: '1fr 1fr',
              my: 4,
              h: '100px'
            }}
          >

            {addonDescription &&
              <>
                <Text fontWeight='bold'>
                  {addonDescription.label}
                </Text>

                <Text fontWeight='bold' ml='auto'>
                  {addonDescription.value}
                </Text>

                <Box
                  sx={{ gridColumn: 'span 2' }}
                  fontSize='sm'
                  overflow='auto'
                >
                  {addonDescription.description}
                </Box>
              </>
            }

            {!addonDescription &&
              <Box
                sx={{
                  display: 'flex',
                  gridColumn: 'span 2',
                  gap: 1
                }}
                alignItems='center'
              >
                <LabelHintIcon color='gray.400' />
                <Text fontSize='lg' color='gray.400'>
                  Najetím myší na doplněk zde zobrazíte blížší informace
                </Text>
              </Box>
            }

          </Grid>

          <Grid
            w='100%'
            // gap={4}
            onMouseLeave={() => setAddonDescription(null)}
          >
            {addons.map(x => {
              return (
                <AddonCheckbox
                  key={x.riskId}
                  disabled={isReadOnly}
                  offer={offer}
                  changedPart={changedRisks[x.packageItemId!]}
                  onHover={(ad) => setAddonDescription(ad)}
                  onChange={(checked, limit, packageItemVariantId) => {
                    setValue('changedParts', {
                      ...changedRisks,
                      [x.packageItemId!]: {
                        packageItem: x,
                        packageItemVariantId,
                        limit,
                        included: checked,
                        riskId: x.riskId
                      } as PartPackage
                    })
                  }}
                  addon={x}
                />
              )
            })}
          </Grid>

          {!isReadOnly &&
            <Button
              ml='auto'
              isDisabled={isLoading}
              isLoading={isLoading}
              colorScheme="green"
              onClick={submitForm}
              mt={4}
              mr={-4}
            >
              Uložit
            </Button>
          }
        </form>
      </FormProvider>
    </>
  )
}